import { z } from "zod";

/**
 * My training (CNT-20) and the completion of an item taken somewhere else
 * (CNT-25) — the first increment of content delivery, ADR-0019 Slice A.
 *
 * There is no player here on purpose. Cohort 1's Related Technical
 * Instruction runs on the vendors' own platforms, so what a learner does
 * in this product is see what is assigned, go to it, and record that it is
 * done with its source. The response types are shared with the portal so a
 * mismatch fails `tsc` rather than a button (qa-battery.md §1.1 rule 2).
 */

/** CNT-01's item types. Only `external_link` is reachable today; the rest arrive with their players. */
export type ContentItemKind = "external_link" | "document" | "video" | "packaged" | "acknowledgement";

/** CNT-02: who made it — the tenant itself, a vendor, or an instructor of the program. Never a tenant's name (ADR-0008, TEN-02). */
export type ContentSource = "tenant" | "vendor" | "instructor";

/**
 * `assigned` → the learner has it; `claimed` → they say it is done and
 * nobody has agreed yet; `completed` → somebody with standing attested it;
 * `withdrawn` → it was taken back, with a reason (CNT-12).
 */
export type AssignmentStatus = "assigned" | "claimed" | "completed" | "withdrawn";

/** CNT-25 / ACC-03: how a completion is evidenced. Every completion says which. */
export type CompletionSource = "vendor_xapi" | "certificate" | "attestation" | "self_report";

export interface TrainingItem {
  id: string;
  kind: ContentItemKind;
  title: string;
  summary: string;
  source: ContentSource;
  /** The vendor's name, when the item is theirs. */
  provider?: string;
  /** Where an external-link item is actually taken (CNT-25). */
  externalUrl?: string;
  durationMinutes?: number;
  /** RTI hours this credits on completion, and it may be 0 (CNT-02). Credited only through a program (CNT-26). */
  rtiHours: number;
  /** What a learner should know about getting at it (CNT-20). */
  accessibilityNote?: string;
}

export interface TrainingCompletion {
  source: CompletionSource;
  claimedAt: string;
  /** The person who said it was done — usually the learner themselves. */
  claimedBy: { id: string; displayName: string };
  evidenceNote?: string;
  attestedAt?: string;
  attestedBy?: { id: string; displayName: string };
  attestedNote?: string;
  /**
   * True when the attester was the person who claimed it. Not refused — the
   * audit row records it as `self_approved` authority and the screen says so,
   * because a director enrolled on their own program is a real case and a
   * silent one would be worse (permission-matrix.md §4.3 governs *gates*,
   * which this is not).
   */
  selfAttested?: boolean;
}

export interface TrainingAssignment {
  id: string;
  item: TrainingItem;
  /** Why it is assigned, in the words the learner reads (CNT-20). Never a code. */
  why: string;
  assignedAt: string;
  dueAt?: string;
  status: AssignmentStatus;
  /** Past its due date and not finished. The server decides this, not the browser's clock. */
  overdue: boolean;
  completion?: TrainingCompletion;
  withdrawnReason?: string;
  /** The program it was assigned under (ADR-0014). Absent: workforce training, which banks no RTI hours (ADR-0018). */
  programId?: string;
  /** What this assignment does to the hours ledger (CNT-26) — decided by the server, said in words by the screen. */
  rti: RtiCredit;
}

/**
 * Where an assignment stands with the hours ledger (CNT-26, REC-18).
 *
 * - `credited` — the completion banked `creditedHours` (net of any correction), dated `creditedOn`.
 * - `pending` — on the program's RTI plan at `planHours`; banks them when a completion is confirmed.
 * - `not_on_plan` — not assigned through a program's RTI-plan entry, so it banks nothing, ever (not a zero-hour entry).
 * - `zero_hours` — on the plan, at 0 hours: nothing to bank.
 * - `not_credited` — confirmed before the ledger existed (dev's first courses); nothing was banked and nothing is
 *   banked retroactively — hours are banked by a confirmation or not at all.
 * - `reversed` — banked, then corrected back to nothing.
 */
export type RtiCreditState = "credited" | "pending" | "not_on_plan" | "zero_hours" | "not_credited" | "reversed";

/** The two RTI kinds the ledger types an entry with (REC-01); OJT joins with work processes. */
export type RtiKind = "rti_paid" | "rti_unpaid";

export interface RtiCredit {
  state: RtiCreditState;
  /** The hours the program's curriculum entry says this is worth — the number that is banked (ADR-0015 §3). */
  planHours?: number;
  /** Net hours in the ledger for this completion, corrections included. */
  creditedHours?: number;
  /** The ledger's `entry_date`: the day the learner said they finished it. */
  creditedOn?: string;
  kind?: RtiKind;
}

/** One program's RTI plan against what the person has banked in it — the running total on My training. */
export interface RtiPlanProgress {
  programId: string;
  programName: string;
  /** The sum of the plan's entries. */
  planHours: number;
  /** Net RTI hours in the ledger for this person in this program. */
  creditedHours: number;
}

export interface MyTrainingResponse {
  assignments: TrainingAssignment[];
  /** What Home needs without counting client-side: due or overdue and not yet claimed. */
  counts: { outstanding: number; overdue: number; awaitingAttestation: number; completed: number };
  /** One per program whose RTI plan this person has something on; empty for somebody with no RTI at all. */
  rtiPlans: RtiPlanProgress[];
}

export interface TrainingAssignmentResponse {
  assignment: TrainingAssignment;
  /** Whether this caller may attest this claim (external_completion.attest, and it is not withdrawn). */
  mayAttest: boolean;
}

/**
 * What the learner sends when they say an external item is done. The note
 * is free text they type — a certificate number, the date they sat it —
 * and must not carry another person's data; the PR template asks.
 */
export const ClaimCompletionSchema = z.object({
  source: z.enum(["vendor_xapi", "certificate", "attestation", "self_report"]).default("self_report"),
  evidenceNote: z.string().trim().max(500).optional(),
});
export type ClaimCompletionRequest = z.infer<typeof ClaimCompletionSchema>;

/**
 * Assigning an item to a person (CNT-10). Only the person target today —
 * the cohort, role and tenant targets, and the recurrence of any cadence,
 * come with the enrolment rules (CNT-11).
 */
export const CreateAssignmentSchema = z.object({
  personId: z.string().uuid(),
  contentItemId: z.string().uuid(),
  /** Why it is assigned, in the words the learner reads (CNT-20). */
  why: z.string().trim().max(500).optional(),
  dueAt: z.string().datetime().optional(),
  /** The program it belongs to (ADR-0014). Without one it is workforce training and credits no RTI hours (CNT-26). */
  programId: z.string().uuid().optional(),
});
export type CreateAssignmentRequest = z.infer<typeof CreateAssignmentSchema>;

export const AttestCompletionSchema = z.object({
  note: z.string().trim().max(500).optional(),
});
export type AttestCompletionRequest = z.infer<typeof AttestCompletionSchema>;

/**
 * One row of Program › Assignments (CNT-10): an assignment with whose it
 * is and which program it counts toward. `program` absent means it was
 * assigned outside a registered program — workforce training (ADR-0018) —
 * and credits no RTI hours whatever the item says (CNT-26).
 */
export interface ProgramAssignment extends TrainingAssignment {
  person: { id: string; displayName: string };
  program?: { id: string; name: string };
  /**
   * The caller manages this row — a program their grant covers, or a
   * workforce-training row they made — rather than seeing it only as their
   * own. Withdraw is offered on these alone.
   */
  managed: boolean;
}

export interface ProgramAssignmentsResponse {
  assignments: ProgramAssignment[];
  /** More matched than one page holds; the screen says so rather than implying it has them all. */
  truncated: boolean;
}

/**
 * What the Assign a course form may offer the caller, and nothing wider:
 * the published catalogue (the tenant's and the platform's, ADR-0015), the
 * people the caller reaches, and the programs `assignment.create` covers
 * for them. Names only for people — the form needs to tell them apart, not
 * to contact them (SEC-01).
 */
export interface AssignableResponse {
  items: TrainingItem[];
  people: Array<{ id: string; displayName: string }>;
  programs: Array<{ id: string; name: string }>;
}

/** CNT-12: taking an assignment back is recorded with its reason, and the reason is required. */
export const WithdrawAssignmentSchema = z.object({
  reason: z.string().trim().min(1).max(500),
});
export type WithdrawAssignmentRequest = z.infer<typeof WithdrawAssignmentSchema>;
